import React, { useState, useMemo } from 'react';
import { Icon } from 'components/ui';
import { debounce } from 'lodash';
import classNames from 'classnames';
import DateFormat from 'constants/date_format.js';
import Tooltip from 'components/shared/Tooltip';
import { Calendar, Lock, Info } from 'react-feather';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash/fp';
import { LESSON_EVENT, routes } from 'constants/config';
import request from 'utils/request_ajax';
import moment, { ISO_8601, now } from 'moment';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import {
  getSeriesRegistrationCap,
  getSeriesTotalRegistered,
} from 'components/InPersonTraining/EventsDashboard/utils';
import PreserveFormatting from 'components/shared/PreserveFormatting';
import SessionDetails from 'components/InPersonTraining/shared/SessionDetails';
import SessionListing from 'components/InPersonTraining/Attending/Registration/SessionListing';
import AttendanceStatus from 'components/InPersonTraining/EventRegistrationOverview/AttendanceStatus';
import AttendanceForm from 'components/InPersonTraining/Attending/Registration/AttendanceForm';
import StepIndicator from './StepIndicator';

const AvailabilityIndicator = ({
  locked,
  displayAddedAt,
  updatedAt,
  addedAfterCompletion,
  attendance,
}) => {
  return (
    <div className="JourneyAvailability">
      {locked && (
        <Tooltip
          position="top"
          text={
            displayAddedAt
              ? t('journeys.viewer.added_after')
              : t('journeys.viewer.locked')
          }
        >
          <Lock className="JourneyAvailability-lock" size={20} />
        </Tooltip>
      )}
      {!locked && <RequiredAttendance attendance={attendance} />}
      {displayAddedAt && (
        <div className="JourneyAvailability-text">
          {`${t('journeys.viewer.added_on')} ${moment(updatedAt).format(
            DateFormat.MONTH_DAY_YEAR_SHORT,
          )}`}
        </div>
      )}
      {addedAfterCompletion && (
        <p className="JourneyAvailability-text">
          {t('journeys.viewer.added_after_completion')}
        </p>
      )}
    </div>
  );
};

const RequiredAttendance = attendance => {
  if (
    attendance.attendance.rsvp_status === 'will_attend' ||
    attendance.attendance.rsvpStatus === 'will_attend'
  ) {
    return (
      <div className="JourneyBuilderContent-attendance">
        {t('journeys.builder.awaiting_attendance')}
      </div>
    );
  }

  return (
    <div className="JourneyBuilderContent-attendance">
      {t('journeys.builder.attendance_required')}
    </div>
  );
};

const ShowMore = ({ children }) => {
  const text = children;
  const [isShowMore, setIsShowMore] = useState(true);
  const toggleShowMore = () => {
    setIsShowMore(!isShowMore);
  };
  return (
    <p className="text" data-testid="JourneyViewerHeader-description">
      <PreserveFormatting>
        {isShowMore
          ? `${text.slice(0, 200)}${text.length > 200 ? '... ' : ' '}`
          : text}
      </PreserveFormatting>
      <>&nbsp;</>
      <button
        className="u-resetBtn"
        type="button"
        onClick={e => {
          e.preventDefault();
          toggleShowMore();
        }}
      >
        <Text
          className="mt-16 h-td-u u-inline"
          variant={TEXT_VARIANTS.BODY_LINK}
        >
          {text.length > 300 && (
            <>{isShowMore ? t('common.show_more') : t('common.show_less')}</>
          )}
        </Text>
      </button>
    </p>
  );
};

const JourneyEventCard = ({
  step,
  index,
  event,
  sessions,
  attendance,
  canChangeAttendance,
  type,
  setNewAttendance,
  previewing,
  userId,
  selectedSessionId,
  setSelectedSessionId,
}) => {
  /* eslint-disable camelcase */
  /*
  Fix snake_case issues. Once all of InPersonTraining/Training
  sends camelCase including in child components, then we can remove this.
  */
  const isPreview = previewing;
  const { completedSteps, currentStep, steps, started, startedAt, completed } =
    useSelector(state => state);

  const stepCompleted = !!step.stepProgress && step.stepProgress.completedAt;
  const inLearnerView = completedSteps !== undefined;
  const addedAfterCompletion = step.stepProgress == null && completed;
  const unavailable =
    step.stepId !== currentStep && !stepCompleted && !addedAfterCompletion;
  const unavailableStyles = { opacity: 0.6 };

  const numberOfSessions = step.event.numberOfSessions
    ? step.event.numberOfSessions
    : step.event.number_of_sessions;

  const nextSession = step.event.nextSession
    ? step.event.nextSession
    : step.event.next_session;

  const rsvpType = step.event.rsvpType
    ? step.event.rsvpType
    : step.event.rsvp_type;

  const hasMultipleSessions = numberOfSessions > 1;
  const moreSessionsCount = hasMultipleSessions ? numberOfSessions - 1 : null;

  const displayAddedAt =
    !completed &&
    startedAt &&
    step.stepUpdatedAt &&
    Date.parse(startedAt) < Date.parse(step.stepUpdatedAt);

  const isPositiveAttendance = ['did_attend', 'will_attend'].includes(
    attendance?.status,
  );
  const isSeries = hasMultipleSessions && event.rsvp_type === 'all';
  const seriesRegistrationCap = isSeries && getSeriesRegistrationCap(sessions);
  const seriesTotalRegistered = isSeries && getSeriesTotalRegistered(sessions);

  function getSelectedSessionId() {
    return !hasMultipleSessions ? sessions[0].id : null;
  }

  const [isModifyingRSVP, setIsModifyingRSVP] = useState(false);
  const [registrationSubmitting, setRegistrationSubmitting] = useState(false);
  const [notAttendingSubmitting, setNotAttendingSubmitting] = useState(false);
  const { past_event } = type;
  const lessonEventPreview = classNames('mb-12', {
    'disabled pe-none': isPreview,
  });
  const currentAndFutureSteps =
    steps.findIndex(x => x.stepId === currentStep) >= index;

  function currentSession() {
    const applicableSession = sessions.find(
      session => session.id === attendance.session_id,
    );
    if (applicableSession) {
      return applicableSession;
    }
    return sessions[0];
  }

  // User has registered for an event as did/will or did_not/will_not attend, OR
  // User has no attendance record for a past event
  function hasRsvpInfo() {
    return (
      (!isEmpty(attendance) &&
        (attendance.did_attend !== null || attendance.rsvp_status !== null) &&
        !isModifyingRSVP) ||
      (isEmpty(attendance) && past_event)
    );
  }

  function isPastEvent() {
    return past_event;
  }

  // User was invited, did not respond, and is unable to change their attendance for the now-past event
  function pastEventUneditable() {
    return past_event && !canChangeAttendance;
  }

  // event has no registration capacity set on any sessions
  function noRegistrationCapacity() {
    if (hasMultipleSessions) {
      return sessions.every(session => session?.max_attendees === null);
    }
    return !hasMultipleSessions && sessions[0]?.max_attendees === null;
  }

  // Sessions all have a max attendance set and all sessions are at capacity
  function eventAtCapacity() {
    if (noRegistrationCapacity()) return false;

    if (hasMultipleSessions) {
      if (event.rsvp_type === 'all') {
        return sessions.some(session => session.type.at_capacity);
      }
      return sessions.every(session => session.type.at_capacity);
    }
    return (
      !hasMultipleSessions &&
      sessions[0]?.total_registered >= sessions[0]?.max_attendees
    );
  }

  function previewEventAtCapacity() {
    if (noRegistrationCapacity()) return false;

    if (hasMultipleSessions) {
      if (event.rsvp_type === 'all') {
        return sessions.some(
          session => session.total_registered >= session.max_attendees,
        );
      }
      return sessions.every(
        session => session.total_registered >= session.max_attendees,
      );
    }
    return (
      !hasMultipleSessions &&
      sessions[0]?.total_registered >= sessions[0]?.max_attendees
    );
  }

  function canChangeRSVP() {
    return (
      !type.past_event ||
      canChangeAttendance ||
      (!type.pastEvent && (!eventAtCapacity() || isPositiveAttendance))
    );
  }

  function futureSessionsAtCapacity() {
    const futureSessions = sessions.filter(session =>
      moment(session.start_time, ISO_8601).isAfter(now()),
    );
    return futureSessions.every(session => session.type.at_capacity);
  }

  function showSessionAtCapacityNotice() {
    if (noRegistrationCapacity()) return false;

    if (isPreview) {
      // preview lesson for single-session event at capacity will display At Capacity banner
      // and will not display banner otherwise
      if (!hasMultipleSessions && eventAtCapacity()) return true;
      if (!hasMultipleSessions && !eventAtCapacity()) return false;

      // in a company that allows learners to change past attendance,
      // preview lesson for a multi-session event at capacity
      // will display At Capacity banner
      if (hasMultipleSessions) {
        if (canChangeAttendance && eventAtCapacity()) return true;

        // in a company that does NOT allow learners to change past attendance,
        // preview lesson for
        // a multi-session event fully at capacity OR
        // a multi-session event with future sessions at capacity
        // will display At Capacity banner
        if (!canChangeAttendance) {
          if (eventAtCapacity()) return true;
          if (!isPastEvent() && futureSessionsAtCapacity()) return true;
        }
      }
    }

    if (event.rsvp_type === 'single') return false;

    // if user has not registered at all AND
    // event is entirely full, then
    // show At Capacity
    if (isEmpty(attendance) && eventAtCapacity() && event.rsvp_type === 'all')
      return true;

    // if user has not registered at all AND
    // event sessions are available AND
    // if multiple sessions AND
    // if user cannot change past attendance AND
    // if future event sessions are full
    // then show At Capacity
    if (
      isEmpty(attendance) &&
      !eventAtCapacity() &&
      hasMultipleSessions &&
      !canChangeAttendance &&
      !past_event &&
      futureSessionsAtCapacity()
    ) {
      return true;
    }

    // if user has already registered as will/did attend, then
    // do not show At Capacity
    if (
      !isEmpty(attendance) &&
      (attendance.did_attend ||
        attendance.rsvp_status === LESSON_EVENT.RSVP_STATUS)
    ) {
      return false;
    }

    // if user is currently registered as did/will NOT attend AND
    if (
      !isEmpty(attendance) &&
      !(
        attendance.did_attend ||
        attendance.rsvp_status === LESSON_EVENT.RSVP_STATUS
      )
    ) {
      // if single session event and event is entirely full,
      // then show At Capacity
      if (!hasMultipleSessions && eventAtCapacity()) {
        return true;
      }

      // if multiple sessions AND
      if (hasMultipleSessions) {
        // if user can change past attendance AND
        // if all future/past event sessions are full,
        // then show At Capacity
        if (canChangeAttendance && eventAtCapacity()) {
          return true;
        }

        // if user cannot change past attendance AND
        if (!canChangeAttendance) {
          // if future event sessions are full
          // then show At Capacity
          if (!past_event && futureSessionsAtCapacity()) {
            return true;
          }

          // if past event sessions are full, then
          // show At Capacity
          if (past_event && eventAtCapacity()) {
            return true;
          }
        }
      }
    }

    // default to not show At Capacity banner
    return false;
  }

  // isPreview may become isLearnerView
  function showAttendanceStatus() {
    if (isPreview && showSessionAtCapacityNotice()) return true;
    if (isPreview && !showSessionAtCapacityNotice()) return false;

    if (isPastEvent()) return true;

    // if user has not registered at all for future event that is not at capacity, do not show a status
    if (isEmpty(attendance) && !isPastEvent() && !showSessionAtCapacityNotice())
      return false;

    // if user has not registered at all for an event that is not at capacity,
    // and for which they would be able to change past attendance, do not show a status
    if (canChangeAttendance && isEmpty(attendance) && !eventAtCapacity())
      return false;

    return (
      hasRsvpInfo() ||
      pastEventUneditable() ||
      eventAtCapacity() ||
      (!isPastEvent() && futureSessionsAtCapacity())
    );
  }

  function showAttendanceControls() {
    if (isPreview && previewEventAtCapacity()) return false;
    if (isPositiveAttendance && !(isPastEvent() && !canChangeAttendance))
      return true;
    if (eventAtCapacity()) return false;
    if (canChangeAttendance && !showSessionAtCapacityNotice()) return true;

    return !isPastEvent() && !futureSessionsAtCapacity();
  }

  function showAddToCalendar() {
    return (
      !type.past_event &&
      !isEmpty(attendance) &&
      attendance.rsvp_status === LESSON_EVENT.RSVP_STATUS
    );
  }

  const isArchived = () => event.archived_at != null;

  function isDisabled() {
    return (
      isPreview ||
      showSessionAtCapacityNotice() ||
      (isPastEvent() && !canChangeAttendance && isEmpty(attendance))
    );
  }

  const debouncedUpdateAttendance = useMemo(() => {
    return debounce(function handleUpdateAttendance(attendanceStatus) {
      const setAttendanceUrl =
        isPastEvent() || event.rsvp_type === 'single'
          ? // To Cleanup: If user clicks "I will not attend" but session hasn't been selected,
            // need to retrieve a valid session id to complete request to new API
            routes.TRAINING.SESSION_SET_ATTENDANCE(
              event.id,
              selectedSessionId || sessions[0].id,
            )
          : routes.TRAINING.EVENT_SET_RSVP(event.id);

      const status = isPastEvent()
        ? {
            attendanceStatus: attendanceStatus
              ? 'did_attend'
              : 'did_not_attend',
          }
        : { rsvpStatus: attendanceStatus ? 'will_attend' : 'will_not_attend' };

      request
        .putJSON(setAttendanceUrl, {
          ...status,
          userId,
          createdFrom: null,
        })
        .then(response => {
          if (response.data) {
            setNewAttendance(prev => !prev);
            setIsModifyingRSVP(false);
            setRegistrationSubmitting(false);
            setNotAttendingSubmitting(false);
          }
        });
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    event.id,
    getSelectedSessionId,
    selectedSessionId,
    setNewAttendance,
    event.rsvp_type,
    sessions,
    userId,
  ]);

  const NoUpcomingEvents = () => {
    if (past_event && !step.event.is_archived) {
      return (
        <div className="JourneyBuilderContent-eventNotification">
          <div className="JourneyBuilderContent-upcomingEventsText">
            <Info className="mr-10" size={18} />
            {t('journeys.event.no_upcoming_event_learner_view')}
          </div>
        </div>
      );
    }
    return null;
  };

  const ArchivedEvents = () => {
    if (step.event.is_archived) {
      return (
        <div className="JourneyBuilderContent-eventNotification">
          <div className="JourneyBuilderContent-archivedEventsText">
            <Icon name="warning_sign" className="mr-10" size={18} />
            {t('journeys.event.archived_event_learner_view')}
          </div>
        </div>
      );
    }
    return null;
  };

  const RegistrationCap = () => {
    if (
      seriesRegistrationCap &&
      seriesRegistrationCap <= seriesTotalRegistered
    ) {
      return (
        <div className="JourneyBuilderContent-eventNotificationRegistration">
          <div className="JourneyBuilderContent-archivedEventsText">
            <Info className="mr-10" size={18} />
            {`${t('journeys.event.registration_cap')}`}
          </div>
        </div>
      );
    }
    return null;
  };

  const RSVPTypeText = rsvptype => {
    if (numberOfSessions <= 1) return null;

    switch (rsvptype) {
      case 'all':
        return (
          <div className="JourneyEventsRSVPText">
            This event includes {numberOfSessions} sessions.
          </div>
        );
      case 'single':
        return (
          <div className="JourneyEventsRSVPText">
            Select a session to attend.
          </div>
        );
      default:
        return null;
    }
  };

  const journeyContent = classNames({
    'JourneyContent-content':
      !inLearnerView || (inLearnerView && !currentAndFutureSteps),
    'JourneyContent-eventDescription': inLearnerView && currentAndFutureSteps,
  });

  return (
    <div className="JourneyContentWrapper" data-testid="JourneyContentWrapper">
      <div
        className="JourneyContent"
        style={unavailable && inLearnerView ? unavailableStyles : {}}
      >
        <div className="w-fill">
          <ArchivedEvents />
          <NoUpcomingEvents />
          <div className="JourneyContent-headerEvent">
            <div
              className="JourneyContent-title-container"
              data-testid="JourneyContent-title"
            >
              <div className="JourneyContent-titleSection">
                <Calendar
                  className="JourneyBuilderContent-calendarIcon"
                  size={20}
                />
                {started === undefined ? (
                  <div className="JourneyContent-title">{step.title}</div>
                ) : (
                  <div className="JourneyContent-step">{step.title}</div>
                )}
              </div>
              {inLearnerView && (
                <div className="JourneyContent-titleSection JourneyContent-titleSection--right">
                  <AvailabilityIndicator
                    locked={unavailable}
                    displayAddedAt={displayAddedAt}
                    updatedAt={step.stepUpdatedAt}
                    addedAfterCompletion={addedAfterCompletion}
                    attendance={attendance}
                  />
                </div>
              )}
            </div>
          </div>
          <div className={journeyContent}>
            {!!step.description && (
              <div className="JourneyContent-description f-smooth">
                <ShowMore>{step.description}</ShowMore>
              </div>
            )}
            {!currentAndFutureSteps && (
              <SessionDetails
                className="JourneyBuilderContent-eventDetails"
                eventId={step.event.id}
                hasMultipleSessions={hasMultipleSessions}
                moreSessionsCount={moreSessionsCount}
                rsvpType={
                  (rsvpType === 0 || rsvpType === 'all') && hasMultipleSessions
                    ? 'all'
                    : 'single'
                }
                session={nextSession || step.event.last_session}
                sessionCount={step.event.numberOfSessions}
                showRegistrationCap={false}
              />
            )}
            <RegistrationCap />
          </div>
          {currentAndFutureSteps && inLearnerView && (
            <>
              {(!showAttendanceStatus() || rsvpType === 'all') &&
                RSVPTypeText(rsvpType)}
              <div className="LessonEventCard SessionDetailCell ml-24 pb-24 mr-24 ">
                <SessionListing
                  sessions={sessions}
                  selectedSessionId={selectedSessionId}
                  handleSessionClick={selectedSession =>
                    setSelectedSessionId(selectedSession.id)
                  }
                  hasMultipleSessions={hasMultipleSessions}
                  attendance={attendance}
                  currentSession={currentSession()}
                  event={event}
                  changingAttendance={isModifyingRSVP}
                  previewing={isDisabled()}
                  isArchived={isArchived()}
                  canChangeAttendance={canChangeAttendance}
                  pastEvent={isPastEvent()}
                  alwaysShowMeetingUrl
                />
              </div>
            </>
          )}
          {showAttendanceStatus() && inLearnerView && (
            <div className="JourneyAttendanceStatus ml-16">
              <AttendanceStatus
                className="JourneyAttendanceStatus"
                attendance={attendance}
                pastEvent={isPastEvent()}
                showSessionAtCapacityNotice={showSessionAtCapacityNotice()}
              />
            </div>
          )}
        </div>
        {inLearnerView &&
          currentAndFutureSteps &&
          showAttendanceControls() &&
          !isArchived() && (
            <div
              className={`JourneyRegistration LessonEventCard--controls d-f ai-c pl-24 ${lessonEventPreview}`}
            >
              <AttendanceForm
                showRsvpButtons={() =>
                  isModifyingRSVP || !hasRsvpInfo() || false
                }
                pastEvent={isPastEvent()}
                handleUpdateAttendance={isAttending => {
                  if (isAttending) {
                    setRegistrationSubmitting(true);
                  } else {
                    setNotAttendingSubmitting(true);
                  }
                  debouncedUpdateAttendance(isAttending);
                }}
                handleChangeAttendance={() => setIsModifyingRSVP(true)}
                canChangeAttendance={!isPreview && canChangeRSVP()}
                eventAtCapacity={eventAtCapacity()}
                showAddToCalendar={showAddToCalendar}
                event={event}
                session={currentSession()}
                selectedSessionId={selectedSessionId}
                attendance={attendance}
                registrationSubmitting={registrationSubmitting}
                notAttendingSubmitting={notAttendingSubmitting}
                showJoinMeetingButton={false}
                previewing={false}
                hasMultipleSessions={hasMultipleSessions}
                isPositiveAttendance={isPositiveAttendance}
              />
            </div>
          )}
      </div>
      <StepIndicator
        {...{
          stepNumber: steps.findIndex(x => x.stepId === step.stepId) + 1,
          status: step.status,
          progress: step.stepProgress,
          id: step.stepId,
        }}
      />
    </div>
  );
};

export default JourneyEventCard;
